package com.yogaaicoach.pages.dashboard

import com.yogaaicoach.common.ui.components.*
import com.yogaaicoach.common.core.extensions.formatDate
import com.yogaaicoach.common.core.extensions.weeksSince
import com.yogaaicoach.common.core.extensions.weeksSinceNow
import kotlinx.datetime.DayOfWeek
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import kotlin.js.json

internal external interface ProgramDashboardPageProps : Props {
    var dashboardData: DashboardData
    var nextWorkout: DashboardItem?
    var onStartWorkout: (workoutId: String, duration: Int, isCompleted: Boolean) -> Unit
    var onNavigateToUpdateTraining: () -> Unit
    var onNavigateToTest: () -> Unit
}

// Pixel-matched against ProgramDashboardScreen.kt's structure (NextWorkoutContainer +
// SummaryComponent + ProgramTimelineScreen).
internal val ProgramDashboardPage = FC<ProgramDashboardPageProps> { props ->
    val data = props.dashboardData

    div {
        className = ClassName("program-dashboard-page")

        FreeTrialBannerIfNeeded()
        TestButtonIfTestEnv(props.onNavigateToTest)

        props.nextWorkout?.let { next -> NextWorkoutSection(next, props.onStartWorkout) }

        SummarySection(data)

        TimelineSection(data, props.onStartWorkout, props.onNavigateToUpdateTraining)
    }
}

private fun ChildrenBuilder.SectionTitleRow(icon: react.FC<Props>, text: String) {
    div {
        className = ClassName("section-title-row")
        icon {}
        span { +text }
    }
    div { className = ClassName("section-divider") }
}

private fun ChildrenBuilder.DataRow(label: String, value: String) {
    div {
        className = ClassName("data-row")
        span { className = ClassName("data-row-label"); +"$label: " }
        span { className = ClassName("data-row-value"); +value }
    }
}

private fun ChildrenBuilder.FocusAreaChips(focusAreas: String) {
    div {
        className = ClassName("focus-area-chips")
        focusAreas.split(", ").forEach { area ->
            span { className = ClassName("focus-area-chip"); +area }
        }
    }
}

private fun DayOfWeek.displayName(): String = name.lowercase().replaceFirstChar { it.uppercase() }

// --- Next workout ---

private fun ChildrenBuilder.NextWorkoutSection(
    item: DashboardItem,
    onStartWorkout: (workoutId: String, duration: Int, isCompleted: Boolean) -> Unit
) {
    div {
        className = ClassName("next-workout-section")
        SectionTitleRow(SportsGymnasticsIcon, "Next Workout")
        div {
            className = ClassName("info-box")
            InfoIcon {}
            span { +"The planned date is just a suggestion — feel free to work out whenever it fits your schedule!" }
        }
        div {
            className = ClassName("next-workout-card")
            div {
                className = ClassName("next-workout-body")
                div {
                    className = ClassName("next-workout-fields")
                    DataRow("Date Planned", "${item.datePlanned.formatDate()} (${item.datePlanned.dayOfWeek.displayName()})")
                    DataRow("Duration", "${item.duration} minutes")
                    div {
                        className = ClassName("data-row")
                        span { className = ClassName("data-row-label"); +"Focus Areas: " }
                        FocusAreaChips(item.focusAreas)
                    }
                }
                div {
                    className = ClassName("next-workout-button-row")
                    button {
                        className = ClassName("primary-button")
                        +"Start Workout"
                        onClick = { onStartWorkout(item.workoutGuid.toString(), item.duration, item.dateCompleted != null) }
                    }
                }
            }
        }
    }
}

// --- Summary ---
// SummaryComponent.kt picks one of two structurally different composables depending on
// isSmallScreen — not just a reflow of the same cards. Both are rendered here; CSS shows exactly
// one per breakpoint (.summary-small/.summary-large), matching the real screen-size branch.

internal fun ChildrenBuilder.SummarySection(data: DashboardData) {
    div {
        className = ClassName("summary-section")
        SectionTitleRow(BarChartIcon, "Your Progress")
        SummarySectionSmall(data)
        SummarySectionLarge(data)
    }
}

// SummaryComponentLarge.kt: ThisWeekCard, then a Row of ConsistencyStreakCard + TotalWorkoutsCard.
private fun ChildrenBuilder.SummarySectionLarge(data: DashboardData) {
    div {
        className = ClassName("summary-large")

        div {
            className = ClassName("this-week-card")
            p { className = ClassName("summary-card-title"); +"This Week" }
            div {
                className = ClassName("week-stats-row")
                WeekStatColumn("${data.thisWeekCompleted} / ${data.thisWeekGoal}", "Workouts", FitnessCenterIcon)
                WeekStatColumn("${data.thisWeekMinutes} min", "Total Time", AccessTimeIcon, secondary = true)
            }
            ProgressBar(data.thisWeekCompleted, data.thisWeekGoal)
            p {
                className = ClassName("summary-card-footnote")
                +"Last week: ${data.lastWeekCompleted} workout(s), ${data.lastWeekMinutes} min"
            }
        }

        div {
            className = ClassName("summary-secondary-row")
            div {
                className = ClassName("summary-secondary-card")
                p { className = ClassName("summary-card-title summary-card-title-medium"); +"Consistency Streak" }
                WeekStatColumn("${data.streakInWeeks}", "weeks", CalendarMonthIcon)
                p { className = ClassName("summary-card-footnote"); +"Weeks with at least one workout" }
            }
            div {
                className = ClassName("summary-secondary-card")
                p { className = ClassName("summary-card-title summary-card-title-medium"); +"Total Workouts" }
                div {
                    className = ClassName("week-stats-row")
                    WeekStatColumn("${data.totalWorkouts}", "Total Workouts", FitnessCenterIcon)
                    WeekStatColumn("${data.totalMinutes / 60} hr ${data.totalMinutes % 60} min", "Total Time", AccessTimeIcon, secondary = true)
                }
                p { className = ClassName("summary-card-footnote"); +"Your all-time training count" }
            }
        }
    }
}

// SummaryComponentSmall.kt: one radial-gradient box — This/Last Week side by side, a divider,
// Totals (workouts + time), a divider, Consistency streak. No separate cards.
private fun ChildrenBuilder.SummarySectionSmall(data: DashboardData) {
    div {
        className = ClassName("summary-small")

        div {
            className = ClassName("summary-small-row")
            WeekStatSection("This Week", data.thisWeekCompleted, data.thisWeekGoal, data.thisWeekMinutes)
            div { className = ClassName("summary-small-column-divider") }
            WeekStatSection("Last Week", data.lastWeekCompleted, data.lastWeekGoal, data.lastWeekMinutes)
        }

        div { className = ClassName("summary-small-divider") }

        div {
            className = ClassName("summary-small-totals-row")
            TotalsStatColumn("Total Workouts", "${data.totalWorkouts}", FitnessCenterIcon)
            TotalsStatColumn("Total Time", "${data.totalMinutes / 60} hr ${data.totalMinutes % 60} min", AccessTimeIcon, secondary = true)
        }

        div { className = ClassName("summary-small-divider") }

        div {
            className = ClassName("summary-small-consistency")
            TotalsStatColumn("Consistency Streak", "${data.streakInWeeks} weeks", CalendarMonthIcon)
        }
    }
}

private fun ChildrenBuilder.WeekStatSection(title: String, completed: Int, goal: Int, minutes: Int) {
    div {
        className = ClassName("week-stat-section")
        p { className = ClassName("week-stat-section-title"); +title }
        div {
            className = ClassName("week-stat-section-rows")
            SummaryRowInfo("Time", "$minutes min", secondary = true)
            SummaryRowInfo("Workouts", "$completed / $goal", secondary = false)
            ProgressBar(completed, goal)
        }
    }
}

private fun ChildrenBuilder.SummaryRowInfo(label: String, value: String, secondary: Boolean) {
    div {
        className = ClassName(if (secondary) "summary-row-info summary-row-info-secondary" else "summary-row-info")
        p { className = ClassName("summary-row-info-value"); +value }
        p { className = ClassName("summary-row-info-label"); +label }
    }
}

// TotalsStatColumn/NumberComponent pass a highlightColor (primary or secondary) that only colors
// the value text — the icon tint is visually overridden by gradientTint()'s own fixed brush, and
// the title/label text always uses a fixed neutral color, regardless of highlightColor.
private fun ChildrenBuilder.TotalsStatColumn(title: String, value: String, icon: react.FC<Props>, secondary: Boolean = false) {
    div {
        className = ClassName("totals-stat-column")
        div {
            className = ClassName("totals-stat-title-row")
            icon {}
            span { +title }
        }
        p { className = ClassName(if (secondary) "totals-stat-value totals-stat-value-secondary" else "totals-stat-value"); +value }
    }
}

private fun ChildrenBuilder.WeekStatColumn(numberRow: String, text: String, icon: react.FC<Props>, secondary: Boolean = false) {
    div {
        className = ClassName("week-stat-column")
        p { className = ClassName(if (secondary) "week-stat-number week-stat-number-secondary" else "week-stat-number"); +numberRow }
        div {
            className = ClassName("week-stat-label-row")
            icon {}
            span { +text }
        }
    }
}

private fun ChildrenBuilder.ProgressBar(completed: Int, goal: Int) {
    val progress = if (goal == 0) {
        if (completed > 0) 1f else 0f
    } else {
        (completed.toFloat() / goal).coerceIn(0f, 1f)
    }
    div {
        className = ClassName("progress-bar-row")
        div {
            className = ClassName("progress-bar-track")
            div {
                className = ClassName("progress-bar-fill")
                // Inline width since it's a continuous value, not a fixed CSS class. CSSProperties
                // is a third-party external interface (kotlin-wrappers' csstype), so a plain
                // kotlin.js.json object is cast to it rather than built through a typed DSL.
                style = json("width" to "${progress * 100}%") as react.CSSProperties
            }
        }
        span { className = ClassName("progress-bar-label"); +"${(progress * 100).toInt()}%" }
    }
}

// --- Timeline ---

private fun ChildrenBuilder.TimelineSection(
    data: DashboardData,
    onStartWorkout: (workoutId: String, duration: Int, isCompleted: Boolean) -> Unit,
    onNavigateToUpdateTraining: () -> Unit,
) {
    div {
        className = ClassName("timeline-container")
        TimelineHeader(data, onNavigateToUpdateTraining)
        data.dashboardItems.forEachIndexed { index, item ->
            TimelineRow(item, index, index == 0, index == data.dashboardItems.lastIndex, onStartWorkout)
        }
    }
}

// HeaderSmall puts "Update Program" next to the completed-count text; HeaderLarge puts it next
// to the title/subtitle instead — a real position swap, not just a reflow, so (like
// SummarySection) both are rendered and CSS shows exactly one per breakpoint.
private fun ChildrenBuilder.TimelineHeader(data: DashboardData, onNavigateToUpdateTraining: () -> Unit) {
    val goalsText = data.trainingProgram?.goals?.map { mainGoalDisplayName(it) }?.let(::joinGoals) ?: ""
    val currentWeek = data.dashboardItems.firstOrNull()?.datePlanned?.weeksSinceNow() ?: 1
    val totalWeeks = if (data.dashboardItems.isNotEmpty()) {
        data.dashboardItems.first().datePlanned.weeksSince(data.dashboardItems.last().datePlanned)
    } else 1
    val subtitle = "Week $currentWeek of $totalWeeks · $goalsText"
    val completed = data.dashboardItems.count { it.dateCompleted != null }
    val completedText = "$completed of ${data.dashboardItems.size} completed"

    div {
        className = ClassName("timeline-header timeline-header-small")
        SectionTitleRow(DateRangeIcon, "Your Training Program")
        p { className = ClassName("timeline-header-subtitle"); +subtitle }
        div {
            className = ClassName("timeline-header-progress-row")
            span { className = ClassName("timeline-header-completed"); +completedText }
            UpdateProgramButton(onNavigateToUpdateTraining)
        }
        ProgressBar(completed, data.dashboardItems.size)
    }

    div {
        className = ClassName("timeline-header timeline-header-large")
        div {
            className = ClassName("timeline-header-large-top-row")
            div {
                className = ClassName("timeline-header-large-title-column")
                SectionTitleRow(DateRangeIcon, "Your Training Program")
                p { className = ClassName("timeline-header-subtitle"); +subtitle }
            }
            UpdateProgramButton(onNavigateToUpdateTraining)
        }
        span { className = ClassName("timeline-header-completed"); +completedText }
        ProgressBar(completed, data.dashboardItems.size)
    }
}

private fun ChildrenBuilder.UpdateProgramButton(onClick: () -> Unit) {
    button {
        className = ClassName("text-button")
        +"Update Program"
        this.onClick = { onClick() }
    }
}

private fun joinGoals(goals: List<String>): String = when (goals.size) {
    0 -> ""
    1 -> goals[0]
    2 -> "${goals[0]} & ${goals[1]}"
    else -> goals.dropLast(1).joinToString(", ") + " & " + goals.last()
}

private fun ChildrenBuilder.TimelineRow(
    item: DashboardItem,
    index: Int,
    isFirst: Boolean,
    isLast: Boolean,
    onStartWorkout: (workoutId: String, duration: Int, isCompleted: Boolean) -> Unit
) {
    val status = when {
        item.dateCompleted != null -> "completed"
        item.isNextWorkout -> "next"
        else -> "upcoming"
    }

    div {
        className = ClassName("timeline-row")

        div {
            className = ClassName("timeline-column")
            div { className = ClassName(if (isFirst) "timeline-connector timeline-connector-hidden" else "timeline-connector") }
            div {
                className = ClassName("timeline-dot timeline-dot-$status")
                when (status) {
                    "completed" -> CheckIcon {}
                    "next" -> PlayArrowIcon {}
                    else -> {}
                }
            }
            div { className = ClassName(if (isLast) "timeline-connector timeline-connector-hidden" else "timeline-connector") }
        }

        div {
            className = ClassName(if (item.isNextWorkout) "day-card day-card-next" else "day-card")

            div {
                className = ClassName("day-card-header")
                span { className = ClassName("day-card-index"); +"Workout ${index + 1}" }
                span {
                    className = ClassName("status-badge status-badge-$status")
                    +when (status) {
                        "completed" -> "Completed"
                        "next" -> "Next Workout"
                        else -> "Upcoming"
                    }
                }
            }

            div {
                className = ClassName("day-card-body")

                div {
                    className = ClassName("day-card-fields")
                    DataRow("Planned", item.datePlanned.formatDate())
                    DataRow("Duration", "${item.duration} minutes")
                    div {
                        className = ClassName("data-row")
                        span { className = ClassName("data-row-label"); +"Focus Areas: " }
                        FocusAreaChips(item.focusAreas)
                    }
                }

                if (status != "upcoming") {
                    div {
                        className = ClassName("day-card-action")
                        when (status) {
                            "completed" -> button {
                                className = ClassName("outlined-small-button")
                                +"View Workout"
                                onClick = { onStartWorkout(item.workoutGuid.toString(), item.duration, true) }
                            }

                            "next" -> button {
                                className = ClassName("primary-button primary-button-small")
                                +"Start"
                                onClick = { onStartWorkout(item.workoutGuid.toString(), item.duration, false) }
                            }
                        }
                    }
                }
            }
        }
    }
}
